import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const FELDER = [
  { key: 'vorwarn_hu',          label: 'HU-Fälligkeit',           icon: '🔧', desc: 'Vorwarnung für Hauptuntersuchung' },
  { key: 'vorwarn_sp',          label: 'SP-Fälligkeit',           icon: '💨', desc: 'Vorwarnung für Sicherheitsprüfung' },
  { key: 'vorwarn_inspektion',  label: 'Nächste Inspektion',      icon: '🔍', desc: 'Vorwarnung für Wartungsinspektion' },
  { key: 'vorwarn_versicherung',label: 'Versicherungsablauf',     icon: '🛡️', desc: 'Vorwarnung für Versicherungsende' },
  { key: 'vorwarn_leasing',     label: 'Leasing-/Vertragsende',  icon: '📄', desc: 'Vorwarnung für Vertragsende (Leasing/Finanzierung)' },
];

// ─── UserForm Modal ───────────────────────────────────────────────────────────

function UserForm({ initial, currentUserId, onSave, onClose }) {
  const [form, setForm] = useState(initial ? {
    name: initial.name, email: initial.email, password: '', role: initial.role
  } : { name: '', email: '', password: '', role: 'mitarbeiter' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError('Name und E-Mail erforderlich.'); return; }
    if (!initial && !form.password) { setError('Passwort ist erforderlich.'); return; }
    setSaving(true);
    try {
      const data = { name: form.name, email: form.email, role: form.role };
      if (form.password) data.password = form.password;
      await onSave(data);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{initial ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
              </div>
              <div className="form-group form-full">
                <label className="form-label">E-Mail *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">{initial ? 'Neues Passwort (leer = unverändert)' : 'Passwort *'}</label>
                <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={initial ? 'Leer lassen um unverändert zu lassen' : ''} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Rolle</label>
                <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)}
                  disabled={initial && initial.id === currentUserId}>
                  <option value="mitarbeiter">Mitarbeiter</option>
                  <option value="admin">Administrator</option>
                </select>
                {initial && initial.id === currentUserId && (
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>Eigene Rolle kann nicht geändert werden.</div>
                )}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Benutzerverwaltung ───────────────────────────────────────────────────────

function Benutzerverwaltung({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.getUsers().then(setUsers).catch(e => setError(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSave = async (data) => {
    if (editUser) await api.updateUser(editUser.id, data);
    else await api.createUser(data);
    setShowForm(false); setEditUser(null); load();
  };

  const handleDelete = async () => {
    try {
      await api.deleteUser(deleteId);
      setDeleteId(null); load();
    } catch (e) {
      alert(e.message);
      setDeleteId(null);
    }
  };

  function formatDateTime(s) {
    if (!s) return '–';
    return s.replace('T', ' ').slice(0, 16);
  }

  const ROLE_BADGE = { 'admin': 'badge-blue', 'mitarbeiter': 'badge-gray' };
  const ROLE_LABEL = { 'admin': 'Administrator', 'mitarbeiter': 'Mitarbeiter' };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>Benutzerverwaltung</span>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditUser(null); setShowForm(true); }}>+ Neuer Benutzer</button>
      </div>

      {error && <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Lade Benutzer…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Name', 'E-Mail', 'Rolle', 'Angelegt am', 'Aktionen'].map(h => (
                  <th key={h} style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', padding: '8px 16px', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '10px 16px', fontWeight: 600, fontSize: 13 }}>
                    {u.name}
                    {u.id === currentUser.id && <span className="badge badge-success" style={{ marginLeft: 8, fontSize: 10 }}>Ich</span>}
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>{u.email}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <span className={`badge ${ROLE_BADGE[u.role] || 'badge-gray'}`}>{ROLE_LABEL[u.role] || u.role}</span>
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-500)' }}>{formatDateTime(u.erstellt_am)}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <div className="td-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditUser(u); setShowForm(true); }}>✏️</button>
                      {u.id !== currentUser.id && (
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(u.id)}>🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <UserForm
          initial={editUser}
          currentUserId={currentUser.id}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditUser(null); }}
        />
      )}

      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Benutzer löschen</span>
              <button className="modal-close" onClick={() => setDeleteId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Benutzer wirklich löschen? Alle aktiven Sitzungen werden beendet.</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleDelete}>Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Rollen-Verwaltung ────────────────────────────────────────────────────────

function RollenVerwaltung({ isAdmin }) {
  const [rollen, setRollen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.getRollen().then(setRollen).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.createRolle({ name: newName.trim() });
      setNewName(''); setShowNew(false); load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.updateRolle(id, { name: editName.trim() });
      setEditId(null); setEditName(''); load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Rolle wirklich löschen?')) return;
    try {
      await api.deleteRolle(id);
      load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>Ansprechpartner-Rollen</span>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowNew(s => !s)}>
            {showNew ? '✕ Abbrechen' : '+ Neue Rolle'}
          </button>
        )}
      </div>

      {error && <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>}

      {isAdmin && showNew && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="form-input"
            placeholder="Rollenname…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            autoFocus
            style={{ maxWidth: 280 }}
          />
          <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving || !newName.trim()}>
            {saving ? 'Speichern…' : 'Hinzufügen'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Lade Rollen…</div>
      ) : rollen.length === 0 ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Noch keine Rollen vorhanden.</div>
      ) : (
        <div>
          {rollen.map(r => (
            <div key={r.id} style={{ padding: '10px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 12 }}>
              {editId === r.id ? (
                <>
                  <input
                    className="form-input"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleEdit(r.id); if (e.key === 'Escape') { setEditId(null); setEditName(''); } }}
                    autoFocus
                    style={{ maxWidth: 280 }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => handleEdit(r.id)} disabled={saving}>Speichern</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditId(null); setEditName(''); }}>Abbrechen</button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{r.name}</span>
                  {isAdmin && (
                    <div className="td-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditId(r.id); setEditName(r.name); }}>✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id)}>🗑️</button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Kontakte CSV-Import ──────────────────────────────────────────────────────

const FELD_LABELS = {
  name: 'Name1 (Firma)', name2: 'Name2 (Zusatz)',
  strasse: 'Straße', plz: 'PLZ', ort: 'Stadt', land: 'Land',
  telefon: 'Telefon', mobil: 'Mobil', email: 'E-Mail', notizen: 'Notizen'
};

function KontakteImport() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);   // { encoding, total, preview, contacts, stats, headerMap, headers }
  const [result, setResult] = useState(null);     // { neu, aktualisiert, uebersprungen, errors }
  const [error, setError] = useState('');

  const reset = () => { setFile(null); setPreview(null); setResult(null); setError(''); };

  const handleFile = async (f) => {
    if (!f) return;
    setError(''); setResult(null); setPreview(null);
    setFile(f);
    setLoading(true);
    try {
      const data = await api.importKontaktePreview(f);
      setPreview(data);
    } catch (e) {
      setError(e.message);
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleCommit = async () => {
    if (!preview) return;
    setLoading(true); setError('');
    try {
      const r = await api.importKontakteCommit(preview.contacts);
      setResult(r);
      setPreview(null);
      setFile(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const mappedFields = preview ? Object.keys(preview.headerMap || {}) : [];
  const unmappedFields = Object.keys(FELD_LABELS).filter(f => !mappedFields.includes(f));

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>
        Kontakte aus CSV importieren
      </div>
      <div style={{ padding: 20 }}>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
          CSV mit Semikolon-Trennzeichen. Zeichenkodierung UTF-8 oder Windows-1252 wird automatisch erkannt.
          Erwartete Spalten: <code>Name1, Name2, Straße, PLZ, Stadt, Land, Telefon, MOBIL, E-Mail, Bemerkung</code>.
          Abgleich erfolgt über <strong>Name1 + PLZ</strong>.
        </p>

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Erfolgsmeldung */}
        {result && (
          <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: 'var(--success)' }}>
              ✓ Import abgeschlossen
            </div>
            <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
              <div><strong style={{ fontSize: 18 }}>{result.neu}</strong> neu angelegt</div>
              <div><strong style={{ fontSize: 18 }}>{result.aktualisiert}</strong> aktualisiert</div>
              <div><strong style={{ fontSize: 18 }}>{result.uebersprungen}</strong> übersprungen</div>
            </div>
            {result.errors && result.errors.length > 0 && (
              <details style={{ marginTop: 12, fontSize: 12 }}>
                <summary style={{ cursor: 'pointer', color: 'var(--danger)' }}>{result.errors.length} Fehler anzeigen</summary>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {result.errors.map((e, i) => <li key={i}>Zeile {e.zeile} ({e.name}): {e.fehler}</li>)}
                </ul>
              </details>
            )}
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={reset}>Weitere Datei importieren</button>
          </div>
        )}

        {/* Drop-Zone */}
        {!preview && !result && (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--gray-200)'}`,
              borderRadius: 10,
              padding: '40px 20px',
              textAlign: 'center',
              background: dragOver ? 'var(--primary-light)' : 'var(--gray-50)',
              transition: 'all .15s'
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
            <div style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 12 }}>
              {loading ? 'Datei wird verarbeitet…' : 'CSV-Datei hierher ziehen oder auswählen'}
            </div>
            <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
              Datei auswählen
              <input
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])}
                disabled={loading}
              />
            </label>
          </div>
        )}

        {/* Vorschau */}
        {preview && !result && (
          <div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 13, flexWrap: 'wrap' }}>
              <div><strong>Datei:</strong> {file?.name}</div>
              <div><strong>Encoding:</strong> {preview.encoding}</div>
              <div><strong>Datensätze:</strong> {preview.total}</div>
              <div style={{ color: 'var(--success)' }}><strong>{preview.stats.neu}</strong> neu</div>
              <div style={{ color: 'var(--primary)' }}><strong>{preview.stats.aktualisiert}</strong> Update</div>
            </div>

            {unmappedFields.length > 0 && (
              <div className="alert alert-warning" style={{ marginBottom: 16, fontSize: 12 }}>
                Nicht zugeordnete Spalten: {unmappedFields.map(f => FELD_LABELS[f]).join(', ')}
              </div>
            )}

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.6px' }}>
              Vorschau (erste 5 Einträge)
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8, marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)' }}>
                    {mappedFields.map(f => (
                      <th key={f} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--gray-200)', fontWeight: 600, color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>
                        {FELD_LABELS[f]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      {mappedFields.map(f => (
                        <td key={f} style={{ padding: '6px 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                          {c[f] || <span style={{ color: 'var(--gray-300)' }}>–</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-secondary" onClick={reset} disabled={loading}>Abbrechen</button>
              <button className="btn btn-primary" onClick={handleCommit} disabled={loading}>
                {loading ? 'Importiere…' : `Import bestätigen (${preview.total} Kontakte)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Einstellungen({ user }) {
  const [tab, setTab] = useState('vorwarnzeiten');
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getEinstellungen().then(setForm).catch(() => setError('Einstellungen konnten nicht geladen werden.'));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.updateEinstellungen(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = user?.role === 'admin';
  const tabs = [['vorwarnzeiten', 'Vorwarnzeiten'], ['rollen', 'Rollen'], ['import', 'CSV-Import']];
  if (isAdmin) tabs.push(['benutzer', 'Benutzerverwaltung']);

  return (
    <div style={{ maxWidth: 780 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Einstellungen</div>
          <div className="page-subtitle">Globale Konfiguration</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--gray-200)' }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 14,
            color: tab === key ? 'var(--primary)' : 'var(--gray-500)',
            borderBottom: `2px solid ${tab === key ? 'var(--primary)' : 'transparent'}`,
            marginBottom: -2, transition: 'color .15s'
          }}>{label}</button>
        ))}
      </div>

      {tab === 'vorwarnzeiten' && (
        <>
          {error && <div className="alert alert-error">{error}</div>}
          {!form ? (
            <div className="spinner">Lade Einstellungen…</div>
          ) : (
            <form onSubmit={handleSave}>
              <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>
                  Vorwarnzeiten für Fälligkeiten
                </div>
                <div style={{ padding: 20 }}>
                  <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 20 }}>
                    Legt fest, wie viele Tage vor einem Fälligkeitsdatum eine Warnung im Dashboard angezeigt wird.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {FELDER.map(f => (
                      <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, alignItems: 'center', padding: '12px 16px', background: 'var(--gray-50)', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-800)', marginBottom: 2 }}>
                            <span style={{ marginRight: 8 }}>{f.icon}</span>{f.label}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{f.desc}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            className="form-input"
                            type="number"
                            min="1"
                            max="365"
                            value={form[f.key] || ''}
                            onChange={e => set(f.key, e.target.value)}
                            style={{ width: 80, textAlign: 'center', fontWeight: 600, fontSize: 16 }}
                          />
                          <span style={{ fontSize: 13, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>Tage vorher</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Speichern…' : '💾 Einstellungen speichern'}
                </button>
                {saved && <span style={{ color: 'var(--success)', fontSize: 13, fontWeight: 600 }}>✓ Gespeichert</span>}
              </div>
            </form>
          )}
        </>
      )}

      {tab === 'rollen' && (
        <RollenVerwaltung isAdmin={isAdmin} />
      )}

      {tab === 'import' && (
        <KontakteImport />
      )}

      {tab === 'benutzer' && isAdmin && (
        <Benutzerverwaltung currentUser={user} />
      )}
    </div>
  );
}
